import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { DUR, EASE, MQ, BP } from '@/lib/motion';
import { TechPattern } from '@/components/brand/TechPattern';
import { LinkedInIcon } from '@/components/icons/social';
import { initialsOf, type Leader } from '@/content/leadership';

gsap.registerPlugin(useGSAP);

interface TeamCarouselProps {
  /** readonly: the content arrays in src/content are declared `as const`. */
  people: readonly Leader[];
  /** Accessible name for the carousel region. */
  label?: string;
  className?: string;
}

/** Card heights. The centred card grows; its neighbours sit back. */
const H_ACTIVE = 420;
const H_REST = 400;

/**
 * Leadership carousel.
 *
 * Hand-rolled on the GSAP stack the rest of the site already uses rather than
 * pulling in a carousel library, which would ship its own CSS and a second
 * animation system alongside GSAP.
 *
 * Desktop shows three portraits, tablet two, and mobile one. The track slides
 * to reveal the active person, keeping them centred on desktop where possible.
 */
export function TeamCarousel({
  people,
  label = 'Our team',
  className = '',
}: TeamCarouselProps) {
  const n = people.length;

  // The founder appears first and receives the initial emphasis.
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);

  /** Wrap into range, so 0 -> n-1 and n-1 -> 0 both work. */
  const go = useCallback((next: number) => setActive(((next % n) + n) % n), [n]);

  useGSAP(
    () => {
      const track = trackRef.current;
      if (!track) return;
      const cards = gsap.utils.toArray<HTMLElement>('[data-card]', track);
      if (!cards.length) return;

      const mm = gsap.matchMedia();

      function positionTrack(animate: boolean) {
        if (!track) return;
        const perView = window.innerWidth >= BP.lg ? 3 : window.innerWidth >= BP.md ? 2 : 1;
        const index = Math.max(0, Math.min(active - (perView === 3 ? 1 : 0), n - perView));
        const step = cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : 0;
        gsap.to(track, {
          x: -step * index,
          duration: animate ? DUR.fast + 0.1 : 0,
          ease: EASE.out,
          overwrite: true,
        });
      }

      // Keep three portraits in view and slide to reveal the remaining team.
      mm.add(MQ.desktop, () => {
        positionTrack(true);
        cards.forEach((card, i) => {
          gsap.to(card, {
            height: i === active ? H_ACTIVE : H_REST,
            opacity: i === active ? 1 : 0.72,
            duration: DUR.fast + 0.1, // ~0.5s
            ease: EASE.out,
          });
        });
      });

      /*
        Below desktop the cards no longer all fit, so translate the track by
        whole cards. Measuring the rendered card and gap is what keeps this
        honest: the basis is a calc() of the container, so a hardcoded
        percentage would drift at every width.

        Clamping to maxIndex stops the track scrolling past the final card and
        leaving dead space on the right.
      */
      mm.add(MQ.belowLg, () => {
        gsap.set(cards, { height: H_REST, opacity: 1 });
        positionTrack(true);
      });

      /*
        Reduced motion: keep navigation functional without tweening. The height is
        set explicitly rather than left to 'auto' — the card's only child that
        affects layout is an absolutely-positioned overlay, so 'auto' collapses
        it to zero and the section renders empty.
      */
      mm.add(MQ.reduced, () => {
        gsap.set(cards, { height: H_REST, opacity: 1 });
        positionTrack(false);
      });

      // Recalculate card widths even when resizing within the same breakpoint.
      let trackWidth = track.clientWidth;
      const resizeObserver = new ResizeObserver(() => {
        if (track.clientWidth === trackWidth) return;
        trackWidth = track.clientWidth;
        positionTrack(false);
      });
      resizeObserver.observe(track);

      /*
        Mirrors Reveal.tsx: reverting on StrictMode's dev remount can leave a
        card parked at 0.72 opacity or a collapsed height, so restore a
        visible resting state explicitly rather than trusting revert alone.
      */
      return () => {
        resizeObserver.disconnect();
        mm.revert();
        gsap.set(cards, { opacity: 1, clearProps: 'height' });
        gsap.set(track, { clearProps: 'transform' });
      };
    },
    { scope: rootRef, dependencies: [active, n] },
  );

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(active - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(active + 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      go(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      go(n - 1);
    }
  }

  /*
    Pointer drag, below lg only (above that nothing translates, so a drag
    would have nothing to do).

    Lenis is NOT a concern here: useLenis bails on (pointer: coarse) and sets
    syncTouch:false, so on any device that touch-drags, Lenis is not running.
    What matters instead is that VERTICAL scrolling must always win. The
    axis lock below plus `touch-action: pan-y` on the track is what does it —
    the browser keeps vertical, we take horizontal, and no listener ever has
    to call preventDefault.
  */
  const drag = useRef({ x: 0, y: 0, t: 0, active: false, locked: false });

  function onPointerDown(e: ReactPointerEvent<HTMLUListElement>) {
    if ((e.target as HTMLElement).closest('a')) return;
    if (window.innerWidth >= BP.lg) return;
    drag.current = { x: e.clientX, y: e.clientY, t: performance.now(), active: true, locked: false };
  }

  function onPointerMove(e: ReactPointerEvent<HTMLUListElement>) {
    const d = drag.current;
    if (!d.active || d.locked) return;

    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return; // below the noise floor

    // Axis lock: a mostly-vertical gesture is a page scroll, not a swipe.
    if (Math.abs(dy) > Math.abs(dx)) {
      d.active = false;
      return;
    }

    d.locked = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }

  function onPointerUp(e: ReactPointerEvent<HTMLUListElement>) {
    const d = drag.current;
    d.active = false;
    if (!d.locked) return;
    d.locked = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);

    const dx = e.clientX - d.x;
    const dt = performance.now() - d.t;
    const width = e.currentTarget.clientWidth / (window.innerWidth >= BP.md ? 2 : 1);
    const velocity = Math.abs(dx) / Math.max(dt, 1);

    // Commit on a decisive drag or a quick flick; otherwise spring back.
    if (Math.abs(dx) > width * 0.2 || velocity > 0.4) {
      go(dx < 0 ? active + 1 : active - 1);
    } else {
      go(active);
    }
  }

  const activePerson = people[active];

  return (
    <div
      ref={rootRef}
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={`relative ${className}`}
    >
      {/*
        Announce only the active person. Putting aria-live on the track would
        make a screen reader re-read all cards on every change.
      */}
      <p aria-live="polite" className="sr-only">
        {activePerson ? `${activePerson.name}, ${activePerson.title}` : ''}
      </p>

      <div className="overflow-hidden">
        <ul
          ref={trackRef}
          // pan-y: the browser keeps vertical scrolling, we take horizontal.
          style={{ touchAction: 'pan-y' }}
          // items-center: the active card is taller, so the shorter neighbours
          // sit centred against it rather than hanging from the top edge.
          className="flex list-none items-center gap-6"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {people.map((person, i) => (
            <li
              key={person.name}
              data-card
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${n}: ${person.name}`}
              onFocus={() => go(i)}
              /*
                h-[400px] is the CSS floor. GSAP overrides it inline, but if JS
                never runs the card still has a height — its only child is an
                absolutely-positioned overlay, so without this it would collapse.

                basis: one card per view on mobile, two at md, three at lg.
                The gap is subtracted so N cards plus (N-1) gutters fit exactly
                in the track rather than overflowing it.
              */
              className="rounded-media relative h-[400px] shrink-0 basis-full overflow-hidden md:basis-[calc((100%-1.5rem)/2)] lg:basis-[calc((100%-3rem)/3)]"
            >
              {/* Portraits fall back to a branded monogram when absent. */}
              {person.photo ? (
                <img
                  src={person.photo}
                  alt={`${person.name}, ${person.title} at Falcon Solutions`}
                  width={400}
                  height={480}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover object-top"
                />
              ) : (
                <div className="relative grid h-full w-full place-items-center bg-linear-to-br from-ink-3 via-ink to-brand-mark">
                  <TechPattern opacity={0.08} size={90} />
                  <span
                    className="font-display relative z-10 text-6xl font-extrabold text-white/90"
                    style={{ fontStretch: '88%' }}
                  >
                    {initialsOf(person)}
                  </span>
                </div>
              )}

              {/*
                Navy scrim, not black — it has to sit inside the brand even
                over a bright headshot. Over the monogram tile it is nearly
                invisible, which is correct: that tile is already dark.
              */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-linear-to-t from-ink-3 from-25% via-ink/80 to-transparent pt-12 pb-3">
                <div className="px-6 text-center">
                  <h3
                    className="font-display text-2xl font-extrabold uppercase text-white"
                    style={{ fontStretch: '88%', textShadow: '0 1px 12px rgb(10 30 55 / 0.6)' }}
                  >
                    {person.name}
                  </h3>
                  <p
                    className="text-caption mt-1 font-medium text-white/75"
                    style={{ textShadow: '0 1px 12px rgb(10 30 55 / 0.6)' }}
                  >
                    {person.title}
                  </p>
                  {person.linkedin && (
                    <a
                      href={person.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${person.name} on LinkedIn (opens in a new tab)`}
                      className="pointer-events-auto mt-1 inline-flex min-h-11 items-center gap-2 rounded px-3 text-sm font-medium text-white underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                    >
                      <LinkedInIcon />
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Arrows: pointer-only affordance, so they stay out of the way on touch. */}
      <button
        type="button"
        onClick={() => go(active - 1)}
        aria-label="Previous team member"
        className="absolute top-1/2 -left-2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-brand text-white transition-colors duration-500 ease-brand hover:bg-brand-deep lg:grid"
      >
        <Chevron direction="left" />
      </button>
      <button
        type="button"
        onClick={() => go(active + 1)}
        aria-label="Next team member"
        className="absolute top-1/2 -right-2 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-brand text-white transition-colors duration-500 ease-brand hover:bg-brand-deep lg:grid"
      >
        <Chevron direction="right" />
      </button>

      {/*
        Dots below lg, where the arrows are hidden — without them a touch user
        who does not discover the swipe has no control at all.
      */}
      <div className="mt-8 flex justify-center gap-3 lg:hidden">
        {people.map((person, i) => (
          <button
            key={person.name}
            type="button"
            onClick={() => go(i)}
            aria-label={`Go to ${person.name}`}
            aria-current={i === active ? 'true' : undefined}
            className="grid h-11 w-11 place-items-center"
          >
            <span
              className={`block h-2 rounded-full transition-all duration-500 ease-brand ${
                i === active ? 'w-8 bg-brand' : 'w-2 bg-ink/25'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

function Chevron({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={direction === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6'} />
    </svg>
  );
}
